// The schedule's view set, and what each one covers.
//
// Phase 7 stage 3 (spec 05-scheduling.md, amended 2026-08-19). Dayboard had a
// month and a week grid; Zanetto Tools has eight views, and Nathan named them as
// the key feature. This file is the single place that knows what a view is —
// its date range, its label, its navigation step and who may use it — so adding
// one is an entry here rather than a hunt through the page.
import type { ResourceKind } from '../../schedulingApi';
import {
  addDays,
  addMonths,
  endOfMonth,
  monthGrid,
  monthLabel,
  formatDate,
  startOfMonth,
  startOfWeek,
  todayISO,
  type DateOnly,
} from '../../lib/dates';

export type ScheduleView =
  | 'agenda'
  | 'week'
  | 'month'
  | '3month'
  | 'timeline'
  | 'crew'
  | 'plant'
  | 'vehicle'
  | 'maintenance';

export interface ViewDef {
  id: ScheduleView;
  label: string;
  /** Field workers never reach this page at all, but managers below admin do. */
  managerOnly?: boolean;
}

export const VIEWS: ViewDef[] = [
  { id: 'agenda', label: 'Agenda' },
  { id: 'week', label: 'Week' },
  { id: 'month', label: 'Month' },
  { id: '3month', label: '3 month' },
  { id: 'timeline', label: 'Timeline' },
  { id: 'crew', label: 'Crew' },
  { id: 'plant', label: 'Plant' },
  // Parity pass 2 batch 3. Vehicle splits out of Plant now that they are
  // separate kinds; Maintenance is a board of the machines rather than a
  // different date range — see maintenanceOnly below.
  { id: 'vehicle', label: 'Vehicles' },
  { id: 'maintenance', label: 'Maintenance' },
];

/** Which resource kind a resource-row view shows. Null for the others. */
export function viewKind(view: ScheduleView): ResourceKind | null {
  if (view === 'crew') return 'person';
  if (view === 'plant') return 'plant';
  if (view === 'vehicle') return 'vehicle';
  return null;
}

export const isResourceTimeline = (view: ScheduleView): boolean =>
  view === 'crew' || view === 'plant' || view === 'vehicle' || view === 'maintenance';

/**
 * The Maintenance view is the ordinary resource timeline with the job bookings
 * filtered out — not a new board.
 *
 * It answers one question: what is off the road, and who has it. Job bookings
 * on those same machines are the answer to a different question and would bury
 * it. Everything else about the board — the rows, the sticky name column, the
 * away shading — is identical, which is why this is a filter rather than a
 * component.
 */
export const maintenanceOnly = (view: ScheduleView): boolean => view === 'maintenance';

/** Rows on the Maintenance board: everything that can go to a mechanic. */
export const MAINTAINABLE_KINDS: ResourceKind[] = ['plant', 'vehicle'];

/**
 * The window a view loads.
 *
 * Agenda is deliberately anchor-independent — it always starts today. It is the
 * "what am I doing" view, and a version of it that could be scrolled back into
 * last March would just be the week view with extra steps.
 */
export function rangeFor(view: ScheduleView, anchor: DateOnly): { from: DateOnly; to: DateOnly } {
  switch (view) {
    case 'agenda': {
      const today = todayISO();
      return { from: today, to: addDays(today, 27) };
    }
    case 'week': {
      const from = startOfWeek(anchor);
      return { from, to: addDays(from, 6) };
    }
    case 'month': {
      const days = monthGrid(anchor);
      return { from: days[0]!, to: days[days.length - 1]! };
    }
    case '3month':
      return { from: startOfMonth(anchor), to: endOfMonth(addMonths(startOfMonth(anchor), 2)) };
    // The boards are ANCHOR-INDEPENDENT since round 22, matching ZT: a fixed
    // window a month back and five months on, auto-scrolled so today is
    // centred. The ‹ Today › nav does not render for them (hasDateNav) —
    // scrolling IS the navigation, and an anchor that moved the whole window
    // would fight the scroll position.
    case 'timeline':
    case 'crew':
    case 'plant':
    case 'vehicle':
    case 'maintenance': {
      const today = todayISO();
      return { from: addDays(today, -TIMELINE_DAYS_BEFORE), to: addDays(today, TIMELINE_DAYS_AFTER) };
    }
  }
}

/** ZT's window: 30 days of recent past, five months of plannable future. */
export const TIMELINE_DAYS_BEFORE = 30;
export const TIMELINE_DAYS_AFTER = 150;

/** Which views the ‹ Today › navigation applies to — the anchored ones. */
export function hasDateNav(view: ScheduleView): boolean {
  return view === 'week' || view === 'month' || view === '3month';
}

/** Where the ‹ › buttons move the anchor. */
export function stepFor(view: ScheduleView, anchor: DateOnly, direction: -1 | 1): DateOnly {
  switch (view) {
    case 'agenda':
      return anchor; // Nothing to step through; it is always from today.
    case 'week':
      return addDays(anchor, direction * 7);
    case 'month':
      return addMonths(anchor, direction);
    case '3month':
      return addMonths(anchor, direction * 3);
    // Unreachable since round 22 — the nav is hidden for the boards
    // (hasDateNav) — but a total switch keeps the type checker honest.
    case 'timeline':
    case 'crew':
    case 'plant':
    case 'vehicle':
    case 'maintenance':
      return anchor;
  }
}

export function labelFor(view: ScheduleView, anchor: DateOnly): string {
  const { from, to } = rangeFor(view, anchor);
  switch (view) {
    case 'agenda':
      return 'Next four weeks';
    case 'week':
      return `Week of ${formatDate(from)}`;
    case 'month':
      return monthLabel(startOfMonth(anchor));
    case '3month':
      return `${monthLabel(from)} to ${monthLabel(to)}`;
    case 'timeline':
    case 'crew':
    case 'plant':
    case 'vehicle':
    case 'maintenance':
      // Unused while the nav is hidden for the boards, kept truthful anyway.
      return `${formatDate(from)} to ${formatDate(to)}`;
  }
}
